<template>
  <div id="campaign-summary">
    <vs-collapse ref="campaignSummeryCollapse" class="p-0" accordion>
      <!-- tracking -->
      <vs-collapse-item class="mb-8" v-if="editing">
        <div class="font-medium text-black" slot="header">
          {{ $t('Tracking') }}
        </div>
        <trackingSummary/>
      </vs-collapse-item>

      <vs-collapse-item class="step-one-summary mb-8" v-if="preview.includes('summary')">
        <div class="font-medium text-black" slot="header">
          {{ $t('Campaign Objective & Type') }}
        </div>
        <Step1Summary :type="form.step1.type_id" :objective="form.step1.objective_id" :editing="editing"/>
      </vs-collapse-item>

      <vs-collapse-item class="mb-8" v-if="preview.includes('details')">
        <div class="font-medium text-black" slot="header">
          {{ $t('Campaign Details') }}
        </div>

        <Step2Summary :data="form.step2" :editing="editing"/>
      </vs-collapse-item>

      <vs-collapse-item class="mb-8" v-if="preview.includes('adsets')">
        <div class="font-medium text-black" slot="header">
          {{ $t('Ad Sets') }}
        </div>

        <Step3Summary :data="form.step3" :editing="editing"/>
      </vs-collapse-item>

      <vs-collapse-item class="mb-8" v-if="preview.includes('budget')">
        <div class="font-medium text-black" slot="header">
          {{ $t('Budget') }}
        </div>

        <Step4Summary :data="form.step3" :objectiveId="form.step1.objective_id" :editing="editing"/>
      </vs-collapse-item>

      <vs-collapse-item class="mb-8" v-if="editing">
        <div class="font-medium text-black" slot="header">
          {{ $t('Ad Customization') }}
        </div>

        <Step5Summary :data="form.step3"/>
      </vs-collapse-item>
    </vs-collapse>

    <div class="page-views-estimation" v-if="isValidEstimation && activeAdsetIndex > -1 && advertisingChannel.supports_traffic_estimate">
      <h5> <h3>{{ $t(`${getAdsetName()}`) }}</h3> {{ $t(`Traffic Estimation`) }}</h5>
      <p>{{ $t('Increasing your targeting options widens your reach and maximizes the opportunities to connect with the right audience.') }}</p>

      <div class="relative">
        <VueSpeedometer
          id="speedometer"
          :maxValue="getAllPageViews()"
          :maxSegmentLabels="0"
          :segments="1000"
          :value="getEstimatedPageViews()"
          :needleHeightRatio="0.5"
          needleColor="black"
          :ringWidth="40"
          endColor="#FF0000"
          startColor="#6BBF00"
          :currentValueText="$t('Targeting')"
        />
        <div class="specific">{{ $t('Specific') }}</div>
        <div class="broad">{{ $t('Broad') }}</div>
      </div>

      <div class="estimation-counter">
        <number
          ref="pageViewsCounter"
          tag="h4"
          :to="getEstimatedPageViews()"
          :duration="2"
          :format="formatEstimatedPageViews"
        /> <span>{{ $t('Estimated Daily Page Views') }}</span>
      </div>
    </div>

    <div class="reach-estimation-card" v-if="currentStep == 4 && advertisingChannel.supports_reach_estimate">
      <div class="card-heading">
        Estimated daily results
        <div class="reach-card__disclaimer" style="border-top: 1px solid #e9eaeb;">
          Based on META data
        </div>
      </div>

      <div v-if="isBudgetSet() && !hasBudgetError && isReachavailable">
        <div v-if="showAdsetBidHint()" class="hint-and-tips">
          <span>
            The <strong>Bid</strong> you entered limits the number of results
            you're likely to get
          </span>
        </div>
        <div>
          <div class="reach-card">
            <div class="reach-card__title">
              <span>Reach</span>
              <vx-tooltip text="This is the number of Accounts Center accounts we estimate you'll reach in your audience each day.
                This has to do with factors like your bid, budget, targeting criteria and ad placements.
                Your actual reach may be higher or lower than this estimate." position="right">
                <vs-icon class="flex justify-center items-center tooltip-icon" icon-pack="speakol" icon="speakol-info"
                  :color="'#2C2C2C'"></vs-icon>
              </vx-tooltip>
            </div>

            <div class="reach-card__heading">
              <span role="heading" aria-level="2">{{
                formatRange(getEstimatedReach()["reach_range"])
                }}</span>
            </div>
            <!-- <div class="progress-bar" style="height: 12px; line-height: 12px; display: flex">
              <div class="progress-bar__segment" style="background-color: rgb(84, 199, 236); width: 15%"></div>
              <div class="progress-bar__segment" style="background-color: rgb(233, 235, 238); width: 85%"></div>
            </div> -->
          </div>
          <div class="reach-card">
            <div class="reach-card__title">
              <span>{{ reachEstimateActionTitle }}</span>
              <vx-tooltip :text="reachEstimateTooltipText"
                position="right">
                <vs-icon class="flex justify-center items-center tooltip-icon" icon-pack="speakol" icon="speakol-info"
                  :color="'#2C2C2C'"></vs-icon>
              </vx-tooltip>
            </div>

            <div class="reach-card__heading">
              <span role="heading" aria-level="2">{{
                formatRange(getEstimatedReach()["clicks_range"])
                }}</span>
            </div>
            <!-- <div class="progress-bar" style="height: 12px; line-height: 12px; display: flex">
              <div class="progress-bar__segment" style="background-color: rgb(84, 199, 236); width: 25%"></div>
              <div class="progress-bar__segment" style="background-color: rgb(233, 235, 238); width: 75%"></div>
            </div> -->
          </div>
        </div>
        <div class="reach-card__disclaimer">
          The accuracy of estimates is based on factors like past campaign data,
          the budget you entered, market data, targeting criteria and ad
          placements. Numbers are provided to give you an idea of performance
          for your budget, but are only estimates and don't guarantee results.
        </div>
      </div>

      <div v-if="!isBudgetSet() || hasBudgetError">
        <div class="reach-card__disclaimer">
          To unlock your daily reach and link click estimates, please set a
          daily budget for your campaign.
        </div>
      </div>
      <div v-else-if="! isReachavailable">
        <div class="reach-card__disclaimer">
          Results are unavailable.
          <vx-tooltip
            text="The accuracy of estimates is based on factors like past campaign data, the budget you entered, market data, targeting criteria and ad placements.
                 Numbers are provided to give you an idea of performance for your budget, but are only estimates and don't guarantee results."
            position="right">
            <vs-icon class="flex justify-center items-center tooltip-icon" icon-pack="speakol" icon="speakol-info"
              :color="'#2C2C2C'"></vs-icon>
          </vx-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Step1Summary from '@/views/campaigns/components/components/Step1Summary.vue'
import Step2Summary from '@/views/campaigns/components/components/Step2Summary.vue'
import Step3Summary from '@/views/campaigns/components/components/Step3Summary.vue'
import Step4Summary from '@/views/campaigns/components/components/Step4Summary.vue'
import Step5Summary from '@/views/campaigns/components/components/Step5Summary.vue'
import trackingSummary from '@/views/campaigns/components/components/trackingSummary.vue'

import Vue from 'vue'
import VueNumber from 'vue-number-animation'
import VueSpeedometer from 'vue-speedometer'
Vue.use(VueNumber)

import { get } from 'lodash'

export default {
  props: {
    form: {
      required: true,
      type: Object
    },
    preview: {
      required: true,
      type: Array
    },
    editing: {
      required: false,
      type: Boolean,
      default: false
    },
    estimation: {
      required: false,
      type: Object
    },
    activeAdsetIndex: {
      required: false,
      type: Number
    },
    advertisingChannel: {
      type: Object,
      required: true
    },
    currentStep: {
      required: true,
      type: Number
    }
  },
  watch: {
    estimation: {
      handler () {
        this.handleEstimatorStyles()
        this.getEstimatedPageViews()
      },
      deep: true
    }
  },
  components: {
    Step1Summary,
    Step2Summary,
    Step3Summary,
    Step4Summary,
    Step5Summary,
    trackingSummary,
    VueSpeedometer,
    VueNumber
  },
  mounted () {
    this.handleEstimatorStyles()
  },
  beforeDestroy () {
    if (this.$refs.campaignSummeryCollapse.$children.length) {
      for (const item of this.$refs.campaignSummeryCollapse.$children) {
        window.removeEventListener('resize', item.changeHeight)
      }
    }
  },
  computed: {
    isValidEstimation () {
      return this.estimation && typeof this.estimation === 'object'
    },
    hasBudgetError () {
      return this.$vErrors.has(
        [this.errors],
        `step4.adsets.${this.activeAdsetIndex}.budget.budget_value`
      )
    },
    isReachavailable () {
      const estimatedReach = this.getEstimatedReach()

      if (!estimatedReach || !estimatedReach.reach_range) {
        return false
      }
      const [start, end] = estimatedReach.reach_range

      return !(start === 0 && end === 0)
    },

    reachEstimateActionTitle () {
      if (this.form.step1.objective_id === this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_TRAFFIC) {
        return 'Link Clicks'
      }

      if (this.form.step1.objective_id === this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_LEADS) {
        return 'Conversions'
      }
    },
    reachEstimateTooltipText () {
      if (this.form.step1.objective_id === this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_TRAFFIC) {
        return 'This is the number of link clicks that we estimate you can get each day based on your campaign performance and estimated daily reach. The actual number of link clicks you get may be higher or lower than this estimate.'
      }

      if (this.form.step1.objective_id === this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_LEADS) {
        return 'This is the number of leads that we estimate you can get each day based on your campaign performance and estimated daily reach. The actual number of leads that you get may be higher or lower than this estimate.'
      }
    }
  },
  methods: {
    getAdsetName () {
      return get(this.form, `step3.adsets[${this.activeAdsetIndex}].name`, '')
    },
    getAllPageViews () {
      return get(this.form, `step3.adsets.${this.activeAdsetIndex}.allPageViews`, 100000000)
    },
    getEstimatedPageViews () {
      return get(this.form, `step3.adsets[${this.activeAdsetIndex}].estimatedPageViews`, 0)
    },
    formatEstimatedPageViews (number) {
      const estimatedPageViews = this.getEstimatedPageViews()
      if (!number || !estimatedPageViews) {
        return '0'
      }

      return this.$options.filters.addComma(number, '', 0)
    },
    handleEstimatorStyles () {
      const estimatorSVG = document.querySelector('#speedometer svg')
      if (!estimatorSVG) {
        return
      }

      estimatorSVG.style.width = null
      estimatorSVG.removeAttribute('width')

      estimatorSVG.style.height = null
      estimatorSVG.removeAttribute('height')

      estimatorSVG.setAttribute('viewBox', '0 0 300 200')
    },
    showAdsetBidHint () {
      return this.form.step3.adsets[this.activeAdsetIndex] && this.form.step3.adsets[this.activeAdsetIndex].showAdsetBidHint
    },
    isBudgetSet () {
      const budgetValue = get(
        this.form,
        `step3.adsets[${this.activeAdsetIndex}].budget.budget_value`,
        null
      )
      return budgetValue !== null && budgetValue !== 0 && budgetValue !== ''
    },
    getEstimatedReach () {
      return this.form.step3.adsets[this.activeAdsetIndex].reach_estimates
    },
    formatRange (range) {
      const [start, end] = range
      return `${Math.floor(start).toLocaleString('en-US', {
        maximumFractionDigits: 1,
        notation: 'compact'
      })} - ${Math.floor(end).toLocaleString('en-US', {
        maximumFractionDigits: 1,
        notation: 'compact'
      })}`
    },
    calculateProgress (range) {
      if (!range || range.length < 2) return 0
      const [start, end] = range
      const avg = (start + end) / 2
      return Math.min((avg / 10000) * 100, 100)
    }
  }
}
</script>

<style lang="scss">
  #campaign-summary {
    position: sticky;
    top: -81px;
    header {
      cursor: pointer;
    }

    .vs-collapse-item {
      background-color: white;
      cursor: default;
    }

    .vs-collapse {
      padding: 0 !important;
    }

    .vs-collapse-item--header {
      padding: 20px !important;
      font-size: 13px !important;
      font-weight: 500;
    }

    .vs-divider-custom {
      display: none !important;
    }

    .con-content--item {
      cursor: default;
    }

    .vx-card__body {
      padding: 0;
    }

    .con-content--item {
      padding: 0;
    }

    .vs-divider-custom {
      margin: 0 0 26px 0;
    }

    .campaign-summary div.vx-card__header > div > h4 {
      font-size: 16px;
    }

    .header-divider .vs-divider {
      margin-top: 0;
    }

    .custom-objective-style {
      padding: 0 20px 0 20px;
    }

    .custom-type-style {
      padding: 0 20px 0 20px;
      color: #000;
    }

    .custom-divider-style {
      padding: 0 20px 0 20px;
    }

    .custom-summary-button-style {
      padding: 0 20px 20px 20px;
    }

    .page-views-estimation {
      padding: 20px;
      background-color: white;

      .specific {
        position: absolute;
        bottom: 15%;
        left: 5%;
        font-weight: 400;
        font-size: 14px;
        color: #2C2C2C;
      }

      .broad {
        position: absolute;
        bottom: 15%;
        right: 7%;
        font-weight: 400;
        font-size: 14px;
        color: #2C2C2C;
      }

      div {
        text-align: center !important;

        svg {
          g > text.current-value {
            font-weight: 500 !important;
            font-size: 16px !important;
            text-align: center !important;
            fill: #2C2C2C !important;
          }
        }
      }

      .estimation-counter {
        h4 {
          font-weight: 400;
          font-size: 3.5vh;
          text-align: center;
          color: #2C2C2C;
        }

        span {
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
        }
      }

      h5 {
        font-weight: 500;
        font-size: 13px;
        h3 {
          text-overflow: ellipsis;
          overflow: hidden;
          width: auto;
          max-width: 100px;
          display: inline-block;
          white-space: nowrap;
          font-weight: 500;
          font-size: 13px;
          height: 12px;
        }
      }

      p {
        color: rgba(0, 0, 0, 0.56);
        font-weight: 400;
        font-size: 13px;
        margin-top: 5px;
      }
    }

    @media (max-width: 1022px) {
      .campaign-summary {
        margin-top: 20px;
      }
    }
  }

  @media (max-width: 768px) {
    .step-one-summary {
      margin-top: 20px;
    }
  }
  .reach-estimation-card {
    .card-heading {
      font-family: Roboto, Arial, sans-serif;
      font-size: 1.125rem;
      line-height: 1.33333;
      letter-spacing: normal;
      font-weight: 700;
      overflow-wrap: normal;
      text-align: left;
      color: rgba(0, 0, 0, 0.85);
      margin-bottom: 20px;
    }
    padding: 20px;
    background-color: white;
    .reach-card {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .reach-card__heading {
        span {
          font-family: Roboto, Arial, sans-serif;
          font-size: 1.5rem;
          line-height: 1.33333;
          letter-spacing: normal;
          font-weight: 900;
          overflow-wrap: normal;
          text-align: left;
          color: rgba(0, 0, 0, 0.85);
        }
      }
      .reach-card__title {
        margin-top: 10px;
        display: flex;
        align-items: center;
      }

      .reach-card__title span {
        font-family: Roboto, Arial, sans-serif;
        font-size: 0.875rem;
        line-height: 1.42857;
        letter-spacing: normal;
        overflow-wrap: normal;
        text-align: left;
        color: rgba(0, 0, 0, 0.85);
      }

      .progress-bar {
        margin: 5px 0px 10px 0px;

        .progress-bar__segment {
          height: 12px;
          line-height: 12px;
        }
      }
    }
    .reach-card__disclaimer {
      font-family: Roboto, Arial, sans-serif;
      font-size: 0.875rem;
      line-height: 1.42857;
      letter-spacing: normal;
      overflow-wrap: normal;
      text-align: left;
      color: rgba(0, 0, 0, 0.56);
      padding-top: 10px;
      display: flex;
      align-items: center;
    }
    .tooltip-icon {
            margin-left: 5px;
      }

    .hint-and-tips {
      font-family: Roboto, Arial, sans-serif;
      font-size: 0.875rem;
      line-height: 1.42857;
      letter-spacing: normal;
      overflow-wrap: normal;
      text-align: left;
      color: rgba(0, 0, 0, 0.85);
      margin: 12px 0px;
      background-color: #f5f7f8;
      padding: 10px;
    }
  }
</style>
